/**
 * FAQ — /business/faq
 * Accordion-style Q&A grouped by category.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

const FAQS = [
  {
    category: 'Getting Started',
    items: [
      { q: 'What is GoGrabJob Business?', a: 'GoGrabJob Business is an AI-powered recruiting operating system for agencies and in-house teams. It combines a full ATS, AI video & voice interviews, explainable role-fit scoring, visual workflow automation, and multilingual support in one connected workspace.' },
      { q: 'Who is GoGrabJob built for?', a: 'We serve recruitment agencies managing multiple client pipelines, in-house HR and talent teams at startups and enterprises, and growing companies that need to scale hiring without scaling headcount.' },
      { q: 'How do I get started?', a: 'Book a demo and our team will walk you through onboarding. Most teams are fully live within 48 hours. No credit card is required for the trial.' },
      { q: 'Is there a free trial?', a: 'Yes — we offer a 14-day full-feature trial with no credit card required. You can import your existing data during the trial period.' },
    ],
  },
  {
    category: 'Product & Features',
    items: [
      { q: 'How does the AI interview work?', a: 'Candidates receive a link and complete structured video or voice responses on their own schedule. Our AI evaluates each response against your defined rubric, generates a transcript, and returns a scored summary — usually within minutes of submission.' },
      { q: 'What is Explainable Role-Fit?', a: "It's a scoring system that parses your job description into weighted requirements and scores each resume against the same rubric. Every score includes a plain-language explanation of which skills matched, which are gaps, and why — so shortlists are defensible to hiring managers and clients." },
      { q: 'Can I customise the pipeline stages?', a: 'Yes. You can create unlimited custom stages per job, rename or reorder existing stages, set stage-specific SLAs, and build workflow rules that trigger actions when candidates enter or exit a stage.' },
      { q: 'What languages are supported for AI interviews?', a: 'We currently support English, Hindi, Hinglish, and several other regional languages. The candidate-facing interface auto-detects language preference. Contact us if you need a specific language not yet listed.' },
    ],
  },
  {
    category: 'Integrations & Data',
    items: [
      { q: 'Which HRIS and job boards do you integrate with?', a: 'We have native connectors for Workday, BambooHR, Keka, LinkedIn, Indeed, and Naukri. We also support Zapier automation and a full REST + GraphQL API for custom integrations.' },
      { q: 'How do you handle data security and GDPR?', a: 'All data is encrypted at rest (AES-256) and in transit (TLS 1.3). We are GDPR-aligned with a DPA available on request, data residency options, and built-in data subject request workflows. See our Trust Center for the full security posture.' },
      { q: 'Can I import my existing candidate database?', a: 'Yes. We support CSV import and direct migration from most popular ATS platforms. Our onboarding team assists with data mapping during the setup process.' },
    ],
  },
  {
    category: 'Pricing & Billing',
    items: [
      { q: 'How is GoGrabJob priced?', a: 'Pricing is based on your team size and the features you need. We offer plans for agencies and in-house teams of all sizes. Contact us or book a demo for a personalised quote.' },
      { q: 'Are there any setup or onboarding fees?', a: 'No setup fees. Onboarding support is included in every plan. Enterprise customers receive a dedicated customer success manager.' },
      { q: 'Can I cancel anytime?', a: 'Monthly plans can be cancelled at any time. Annual plans may be cancelled with 30 days notice. Your data remains accessible and exportable for 30 days after cancellation.' },
    ],
  },
  {
    category: 'Support',
    items: [
      { q: 'What support options are available?', a: 'All plans include email support and access to our Help Center documentation. Pro and Enterprise plans include live chat and priority response. Enterprise plans include a dedicated account manager.' },
      { q: 'Do you offer training for new teams?', a: 'Yes. We provide onboarding walkthroughs, video tutorials, and a comprehensive knowledge base. Enterprise customers receive live training sessions for their team.' },
      { q: 'What is your uptime SLA?', a: "We maintain 99.97% rolling 90-day uptime for the core API with 24/7 on-call coverage for P1 incidents. You can monitor live service health at our status page." },
    ],
  },
];

function FaqItem({ q, a, idx }: { q: string; a: string; idx: number }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: 0.03 * idx }}
      className={[
        'rounded-xl border transition-colors',
        open
          ? 'border-violet-200 bg-violet-50/50 dark:border-violet-800/50 dark:bg-violet-950/20'
          : 'border-gray-100 bg-white hover:border-gray-200 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700',
      ].join(' ')}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="text-[15px] font-semibold leading-snug text-gray-800 dark:text-zinc-100">{q}</span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 ${open ? 'rotate-180 text-violet-500' : ''}`}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-[15px] leading-[1.75] text-gray-500 dark:text-zinc-400">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function FaqPage() {
  return (
    <B2BMarketingPageLayout>
      {/* Hero */}
      <section
        className="relative overflow-hidden px-4 pb-12 pt-14 sm:px-6 md:px-8 md:pt-18"
        style={{ background: 'linear-gradient(150deg, #F5F3FF 0%, #EDE9FE 35%, #F5F3FF 65%, #EEF2FF 100%)' }}
      >
        <div className="mx-auto max-w-xl text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100">
            <HelpCircle className="h-6 w-6 text-violet-600" aria-hidden />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Frequently Asked Questions
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-500">
            Can't find your answer here? Reach out via our{' '}
            <a href="/business/contact-us" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-600 hover:underline">
              contact form
            </a>{' '}
            or visit the{' '}
            <a href="/business/help-center" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-600 hover:underline">
              Help Center
            </a>.
          </p>
        </div>
      </section>

      {/* FAQ groups */}
      <section className="px-4 py-14 sm:px-6 md:px-8">
        <div className="mx-auto max-w-2xl space-y-14">
          {FAQS.map((group) => (
            <div key={group.category}>
              <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-600 dark:text-violet-400">
                {group.category}
              </h2>
              <div className="space-y-2.5">
                {group.items.map((item, i) => (
                  <FaqItem key={item.q} q={item.q} a={item.a} idx={i} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mx-auto mt-16 max-w-2xl rounded-2xl border border-violet-100 bg-violet-50 p-8 text-center dark:border-violet-900/30 dark:bg-violet-950/20">
          <p className="text-base font-semibold text-gray-800 dark:text-zinc-100">Still have questions?</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
            Reach our team directly — we reply within one business day.
          </p>
          <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <a
              href="/business/contact-us"
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 transition-colors"
            >
              Contact us
            </a>
            <a
              href="/business/help-center"
              className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-5 py-2.5 text-sm font-semibold text-violet-700 hover:bg-violet-50 transition-colors dark:border-violet-800 dark:bg-zinc-900 dark:text-violet-400"
            >
              Browse Help Center
            </a>
          </div>
        </div>
      </section>
    </B2BMarketingPageLayout>
  );
}
